import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { ScrollArea } from '../components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Lightning, SoccerBall, Trophy, Calendar, User, Fire, ArrowCircleRight, ArrowCounterClockwise, FastForward, GlobeHemisphereWest, ArrowsLeftRight, Check, X, Medal, Star } from '@phosphor-icons/react';
import { calculateStandings, getTeamOverall } from '../utils/gameData';
import {
  simulateOtherMatches, generateMatchDecisions, resolveDecision, applyDecisionOutcome,
  computePlayerRating, applyPlayerMatchResult, advanceCareerSeason,
  generateAutoMatchEvents, generateMatchGoalEvents,
  evaluateCallup, simulateInternationalMatch, pickIntlOpponent, shouldTriggerIntlWindow, applyInternationalMatch,
  acceptTransferOffer, isEligibleForCallup,
  getUpcomingBonusFixtures
} from '../utils/playerCareer';
import { buildTeamOfTheWeek, computeSeasonAwards } from '../utils/awards';
import { buildChampionsLeagueTeams, buildEuropaLeagueTeams, buildConferenceLeagueTeams, buildCWCTeams } from '../utils/realTeams';
import { SeasonEndModal } from '../components/SeasonEndModal';

export const PlayerCareerPage = ({ careerState, onUpdateCareer, onResetCareer }) => {
  const navigate = useNavigate();

  const [matchModal, setMatchModal] = useState(null);
  const [matchResultModal, setMatchResultModal] = useState(null);
  const [intlModal, setIntlModal] = useState(null);
  const [transferModal, setTransferModal] = useState(null);
  const [seasonEndModal, setSeasonEndModal] = useState(false);
  const tickerRef = useRef(null);

  const team = useMemo(() => careerState.teams.find(t => t.id === careerState.playerTeamId), [careerState]);

  // ============================================================================
  // International team pools — lazy-built for old career saves that don't have them
  // This guarantees UCL/UEL/UECL/CWC group stages always use real international teams
  // (Madrid Royal, Munich FC, Catalonia FC, Paris Saints, Riyadh Hilal, etc.)
  // ============================================================================
  const intlPools = useMemo(() => {
    const c = careerState.competitions || {};
    return {
      uclTeams:  (c.uclTeams  && c.uclTeams.length)  ? c.uclTeams  : buildChampionsLeagueTeams(),
      uelTeams:  (c.uelTeams  && c.uelTeams.length)  ? c.uelTeams  : buildEuropaLeagueTeams(),
      ueclTeams: (c.ueclTeams && c.ueclTeams.length) ? c.ueclTeams : buildConferenceLeagueTeams(),
      cwcTeams:  (c.cwcTeams  && c.cwcTeams.length)  ? c.cwcTeams  : buildCWCTeams(),
    };
  }, [careerState.competitions]);

  // Unified lookup: find any team by id across EWFL + all international pools
  const teamById = useMemo(() => {
    const m = new Map();
    careerState.teams.forEach(t => m.set(t.id, t));
    [intlPools.uclTeams, intlPools.uelTeams, intlPools.ueclTeams, intlPools.cwcTeams].forEach(pool => {
      pool.forEach(t => { if (!m.has(t.id)) m.set(t.id, t); });
    });
    return m;
  }, [careerState.teams, intlPools]);

  const poolForComp = (compKey) => {
    if (compKey === 'championsLeague') return intlPools.uclTeams;
    if (compKey === 'europaLeague')    return intlPools.uelTeams;
    if (compKey === 'conferenceLeague') return intlPools.ueclTeams;
    if (compKey === 'cwc')             return intlPools.cwcTeams;
    return careerState.teams;
  };
  const player = useMemo(() => team?.players.find(p => p.id === careerState.playerId), [team, careerState.playerId]);
  const standings = useMemo(() => calculateStandings(careerState.teams), [careerState.teams]);
  const rank = team ? standings.findIndex(t => t.id === team.id) + 1 : 0;

  // Auto-open transfer modal when state has pendingTransfer offers
  useEffect(() => {
    if (careerState.pendingTransfer?.offers?.length && !transferModal) {
      setTransferModal(careerState.pendingTransfer);
    }
  }, [careerState.pendingTransfer, transferModal]);

  // Scroll ticker to bottom on new events
  useEffect(() => {
    if (tickerRef.current) tickerRef.current.scrollTop = tickerRef.current.scrollHeight;
  }, [matchModal?.matchState?.tickerEvents?.length]);

  // Build cohesive upcoming + played fixture sets across ALL competitions
  const buildFixtureMeta = (f, compKey) => {
    const map = {
      league:           { lbl: 'LGE',  color: '#D4A933', bg: 'bg-[#D4A933]', text: 'text-black' },
      cup:              { lbl: 'CUP',  color: '#C8102E', bg: 'bg-[#C8102E]', text: 'text-white' },
      championsLeague:  { lbl: 'ASIAN',  color: '#CCFF00', bg: 'bg-[#CCFF00]', text: 'text-black' },
      europaLeague:     { lbl: 'ASIAN', color: '#CCFF00', bg: 'bg-[#CCFF00]', text: 'text-black' },
      conferenceLeague: { lbl: 'ASIAN', color: '#CCFF00', bg: 'bg-[#CCFF00]', text: 'text-black' },
      cwc:              { lbl: 'CWC',  color: '#2E5AAC', bg: 'bg-[#2E5AAC]', text: 'text-white' },
      worldCup:         { lbl: 'WC',   color: '#AF52DE', bg: 'bg-[#AF52DE]', text: 'text-white' }
    };
    const m = map[compKey] || { lbl: compKey?.toUpperCase() || '?', color: '#6E6E73', bg: 'bg-[#6E6E73]', text: 'text-white' };
    return { ...f, _comp: compKey, _compLabel: m.lbl, _compColor: m.color, _compBg: m.bg, _compTextColor: m.text };
  };

  const allUpcoming = useMemo(() => {
    const leagueFixtures = careerState.fixtures
      .filter(f => !f.played && (f.homeTeamId === team?.id || f.awayTeamId === team?.id))
      .map(f => buildFixtureMeta(f, 'league'));
    const bonus = getUpcomingBonusFixtures(careerState).map(f => buildFixtureMeta(f, f.competition));
    return [...leagueFixtures, ...bonus].sort((a, b) => a.week - b.week);
  }, [careerState, team]);

  const upcoming = allUpcoming.slice(0, 8); // for the small "next match" panel

  // Played fixtures per comp (for Standings/Fixtures comp tabs)
  const playedByComp = useMemo(() => {
    const out = { league: [], cup: [], championsLeague: [], europaLeague: [], conferenceLeague: [], cwc: [], worldCup: [] };
    careerState.fixtures.forEach(f => {
      if (f.played && (f.homeTeamId === team?.id || f.awayTeamId === team?.id)) out.league.push(buildFixtureMeta(f, 'league'));
    });
    const comps = careerState.competitions || {};
    const bucketToKey = { cup: 'cup', ucl: 'championsLeague', uel: 'europaLeague', uecl: 'conferenceLeague', cwc: 'cwc', wc: 'worldCup' };
    Object.entries(bucketToKey).forEach(([bucket, compKey]) => {
      (comps[bucket]?.fixtures || []).forEach(f => {
        if (f.played) out[compKey].push(buildFixtureMeta(f, compKey));
      });
    });
    return out;
  }, [careerState, team]);

  const upcomingByComp = useMemo(() => {
    const out = { league: [], cup: [], championsLeague: [], europaLeague: [], conferenceLeague: [], cwc: [], worldCup: [] };
    allUpcoming.forEach(f => { if (out[f._comp]) out[f._comp].push(f); });
    return out;
  }, [allUpcoming]);

  const nextMatch = upcoming[0];
  const seasonComplete = careerState.fixtures.every(f => f.played);

  const startMatch = () => {
    if (!nextMatch) return;
    // Use unified teamById lookup so UCL/UEL/UECL/CWC matches resolve correctly
    const home = teamById.get(nextMatch.homeTeamId) || careerState.teams.find(t => t.id === nextMatch.homeTeamId);
    const away = teamById.get(nextMatch.awayTeamId) || careerState.teams.find(t => t.id === nextMatch.awayTeamId);
    if (!home || !away) {
      console.error('Match aborted: could not find teams', nextMatch, !!home, !!away);
      return;
    }
    const decisions = generateMatchDecisions();
    const baseGoalEvents = generateMatchGoalEvents(home, away, team.id);
    setMatchModal({
      fixture: nextMatch,
      home, away,
      decisions,
      idx: 0,
      baseGoalEvents,
      competition: nextMatch._comp || 'league',
      matchState: {
        homeScore: 0, awayScore: 0,
        playerGoals: 0, playerAssists: 0,
        playerRatingDelta: 0,
        playerEvents: [],
        matchEvents: [],
        tickerEvents: [{ minute: 0, type: 'kickoff', commentary: `Kick off! ${home.name} vs ${away.name}${nextMatch._compLabel && nextMatch._comp !== 'league' ? ` (${nextMatch._compLabel})` : ''}` }],
        decisionsMade: [],
        currentMinute: 0,
        currentDecisionMinute: decisions[0]?.atMinute || 0,
        processedGoalMinutes: new Set()
      }
    });
    setTimeout(() => advanceTicker(0, decisions[0].atMinute), 50);
  };

  const advanceTicker = (fromMin, toMin) => {
    setMatchModal(prev => {
      if (!prev) return prev;
      const { home, away, baseGoalEvents, matchState } = prev;
      // Auto commentary events
      const autoEvents = generateAutoMatchEvents(home, away, team, fromMin, toMin);
      // Goals occurring in this window (excluding player-driven ones)
      const processed = new Set(matchState.processedGoalMinutes || []);
      const newGoals = baseGoalEvents.filter(g => g.minute > fromMin && g.minute <= toMin && !processed.has(g.minute));
      let hs = matchState.homeScore;
      let as = matchState.awayScore;
      const goalEventsWithCtx = newGoals.map(g => {
        if (g.team === 'home') hs++; else as++;
        processed.add(g.minute);
        return { ...g, scoreAfter: `${hs}-${as}` };
      });
      const allNew = [...autoEvents, ...goalEventsWithCtx].sort((a, b) => a.minute - b.minute);
      return {
        ...prev,
        matchState: {
          ...matchState,
          homeScore: hs,
          awayScore: as,
          tickerEvents: [...(matchState.tickerEvents || []), ...allNew],
          matchEvents: [...(matchState.matchEvents || []), ...goalEventsWithCtx],
          currentMinute: toMin,
          processedGoalMinutes: processed
        }
      };
    });
  };

  const handleDecision = (option) => {
    if (!matchModal) return;
    const { decisions, idx, matchState, home, away, fixture } = matchModal;
    const decision = decisions[idx];
    const teamCtx = { ...team, isHome: home.id === team.id };
    const opponent = home.id === team.id ? away : home;
    const result = resolveDecision(player, option);
    const minute = decision.atMinute;

    let nextState = applyDecisionOutcome(
      { ...matchState, currentDecisionMinute: minute },
      player, teamCtx, opponent, option, result
    );
    nextState.decisionsMade = [...(nextState.decisionsMade || []), {
      minute, scenario: decision.scenario, choice: option.label, risk: option.risk, success: result.success
    }];
    // Add decision summary to ticker
    const tickerEntry = {
      minute, type: result.success ? 'decision_success' : 'decision_fail',
      commentary: `${result.success ? '✓' : '✗'} You chose: ${option.label}${result.success ? ' — it worked.' : ' — didn\'t come off.'}`
    };
    // Include any player event generated at this minute (goals/assists)
    const playerEventsThisMinute = nextState.playerEvents.filter(e => e.minute === minute);
    const playerTickerEvents = playerEventsThisMinute.map(e => ({
      minute: e.minute, type: e.type,
      commentary: e.commentary,
      scoreAfter: `${nextState.homeScore}-${nextState.awayScore}`
    }));
    nextState.tickerEvents = [...(nextState.tickerEvents || []), tickerEntry, ...playerTickerEvents];

    const nextIdx = idx + 1;
    if (nextIdx < decisions.length) {
      const nextMinute = decisions[nextIdx].atMinute;
      nextState.currentDecisionMinute = nextMinute;
      setMatchModal({ ...matchModal, idx: nextIdx, matchState: nextState });
      // Advance ticker to next decision
      setTimeout(() => advanceTicker(minute, nextMinute), 400);
    } else {
      // Advance ticker to end of match
      setMatchModal({ ...matchModal, idx: nextIdx, matchState: nextState });
      setTimeout(() => {
        advanceTicker(minute, 90);
        setTimeout(() => finishMatch(home, away, fixture), 300);
      }, 400);
    }
  };

  const finishMatch = (home, away, fixture) => {
    setMatchModal(prev => {
      if (!prev) return prev;
      const { matchState, competition } = prev;
      const homeOvr = getTeamOverall(home);
      const awayOvr = getTeamOverall(away);
      const isHomeTeam = home.id === team.id;
      const opponentOvr = isHomeTeam ? awayOvr : homeOvr;
      const teamOvr = isHomeTeam ? homeOvr : awayOvr;
      const rating = computePlayerRating(player, matchState, opponentOvr, teamOvr);

      const finalResult = {
        ...matchState,
        playerRating: rating,
        competition: competition || 'league'
      };

      const nextCareer = applyPlayerMatchResult(careerState, finalResult, fixture, home, away);
      const othersSim = (competition === 'league')
        ? simulateOtherMatches(nextCareer.teams, nextCareer.fixtures, fixture.week, team.id)
        : { teams: nextCareer.teams, fixtures: nextCareer.fixtures };

      // Build TotW for this week (league only)
      let totwHistory = [...(nextCareer.totwHistory || [])];
      if (competition === 'league') {
        const totw = buildTeamOfTheWeek(
          othersSim.teams, othersSim.fixtures, fixture.week,
          {
            playerId: player.id, teamId: team.id, teamName: team.name,
            playerName: player.name, position: player.position, flag: player.flag,
            rating, goals: matchState.playerGoals || 0, assists: matchState.playerAssists || 0
          }
        );
        if (totw.players.length >= 11) totwHistory.push(totw);
      }

      let updatedCareer = {
        ...nextCareer,
        teams: othersSim.teams,
        fixtures: othersSim.fixtures,
        totwHistory,
        currentWeek: Math.max(nextCareer.currentWeek || 1, fixture.week + 1)
      };

      setTimeout(() => checkInternationalCallup(updatedCareer, rating, fixture.week + 1), 100);

      onUpdateCareer(updatedCareer);
      setMatchResultModal({
        result: finalResult,
        fixture, home, away,
        playerTeamIsHome: isHomeTeam,
        compLabel: fixture._compLabel || 'LEAGUE'
      });
      return null;
    });
  };

  const checkInternationalCallup = (updatedCareer, recentRating, newWeek) => {
    // Trigger at preset international break weeks
    if (!shouldTriggerIntlWindow(newWeek)) return;
    if (!isEligibleForCallup(player)) return;
    const calledUp = evaluateCallup(player, recentRating);
    if (!calledUp) return;
    const opponent = pickIntlOpponent(player.nationality);
    const intlMatch = simulateInternationalMatch(player, newWeek, updatedCareer.season || 1, opponent);
    setIntlModal({ intlMatch, playerNation: player.nationality });
  };

  const acceptIntlMatch = () => {
    if (!intlModal) return;
    const updated = applyInternationalMatch(careerState, intlModal.intlMatch);
    onUpdateCareer(updated);
    setIntlModal(null);
  };

  const skipMatch = () => {
    if (!nextMatch) return;
    const home = teamById.get(nextMatch.homeTeamId) || careerState.teams.find(t => t.id === nextMatch.homeTeamId);
    const away = teamById.get(nextMatch.awayTeamId) || careerState.teams.find(t => t.id === nextMatch.awayTeamId);
    if (!home || !away) {
      console.error('Skip aborted: could not find teams', nextMatch);
      return;
    }
    const baseGoalEvents = generateMatchGoalEvents(home, away, team.id);
    let hs = 0, as = 0;
    baseGoalEvents.forEach(g => { if (g.team === 'home') hs++; else as++; });
    const matchState = {
      homeScore: hs, awayScore: as,
      playerGoals: 0, playerAssists: 0,
      playerRatingDelta: 0,
      playerEvents: [],
      matchEvents: baseGoalEvents,
      tickerEvents: [],
      decisionsMade: [],
      currentDecisionMinute: 0
    };
    if (['ST','CF','LW','RW','SS','CAM','LF','RF'].includes(player.position)) {
      if (Math.random() < 0.25) { matchState.playerGoals = 1; if (home.id === team.id) matchState.homeScore++; else matchState.awayScore++; }
      if (Math.random() < 0.18) { matchState.playerAssists = 1; if (home.id === team.id) matchState.homeScore++; else matchState.awayScore++; }
    } else if (['CM','CDM','LM','RM'].includes(player.position)) {
      if (Math.random() < 0.10) { matchState.playerGoals = 1; if (home.id === team.id) matchState.homeScore++; else matchState.awayScore++; }
      if (Math.random() < 0.22) { matchState.playerAssists = 1; if (home.id === team.id) matchState.homeScore++; else matchState.awayScore++; }
    }
    const homeOvr = getTeamOverall(home);
    const awayOvr = getTeamOverall(away);
    const isHomeTeam = home.id === team.id;
    const opponentOvr = isHomeTeam ? awayOvr : homeOvr;
    const teamOvr = isHomeTeam ? homeOvr : awayOvr;
    const rating = computePlayerRating(player, matchState, opponentOvr, teamOvr);
    const competition = nextMatch._comp || 'league';
    const finalResult = { ...matchState, playerRating: rating, competition };
    const nextCareer = applyPlayerMatchResult(careerState, finalResult, nextMatch, home, away);
    const othersSim = (competition === 'league')
      ? simulateOtherMatches(nextCareer.teams, nextCareer.fixtures, nextMatch.week, team.id)
      : { teams: nextCareer.teams, fixtures: nextCareer.fixtures };
    let totwHistory = [...(nextCareer.totwHistory || [])];
    if (competition === 'league') {
      const totw = buildTeamOfTheWeek(
        othersSim.teams, othersSim.fixtures, nextMatch.week,
        { playerId: player.id, teamId: team.id, teamName: team.name, playerName: player.name, position: player.position, flag: player.flag, rating, goals: matchState.playerGoals || 0, assists: matchState.playerAssists || 0 }
      );
      if (totw.players.length >= 11) totwHistory.push(totw);
    }
    const updatedCareer = {
      ...nextCareer, teams: othersSim.teams, fixtures: othersSim.fixtures,
      totwHistory,
      currentWeek: Math.max(nextCareer.currentWeek || 1, nextMatch.week + 1)
    };
    onUpdateCareer(updatedCareer);
    setTimeout(() => checkInternationalCallup(updatedCareer, rating, nextMatch.week + 1), 100);
    setMatchResultModal({
      result: finalResult,
      fixture: nextMatch,
      home, away,
      playerTeamIsHome: isHomeTeam,
      compLabel: nextMatch._compLabel || 'LEAGUE'
    });
  };

  const advanceSeason = () => {
    // Show the season-end celebration modal. The real advance runs on Continue.
    setSeasonEndModal(true);
  };

  const confirmAdvanceSeason = () => {
    const nextCareer = advanceCareerSeason(careerState);
    onUpdateCareer(nextCareer);
    setSeasonEndModal(false);
  };

  const acceptTransfer = (offer) => {
    if (!window.confirm(`Sign for ${offer.teamName} at £${offer.wage}k/week for ${offer.contractYears} years?`)) return;
    const updated = acceptTransferOffer(careerState, offer);
    onUpdateCareer(updated);
    setTransferModal(null);
  };
  const rejectAllTransfers = () => {
    if (!window.confirm('Reject all offers and stay at your current club?')) return;
    onUpdateCareer({ ...careerState, pendingTransfer: null });
    setTransferModal(null);
  };

  if (!team || !player) {
    return (
      <div className="min-h-screen bg-[#04060F] pt-20 p-8 text-white">
        <p>Career not initialized. <button onClick={() => navigate('/player-career/select')} className="text-[#32D74B] underline">Pick a player</button>.</p>
      </div>
    );
  }

  const seasonMatches = (careerState.matchHistory || []).filter(m => m.season === (careerState.season || 1));
  const seasonGoals = seasonMatches.reduce((s, m) => s + (m.playerGoals || 0), 0);
  const seasonAssists = seasonMatches.reduce((s, m) => s + (m.playerAssists || 0), 0);
  const seasonAvgRating = seasonMatches.length ? (seasonMatches.reduce((s, m) => s + (m.playerRating || 0), 0) / seasonMatches.length) : 0;
  const intl = careerState.international || { caps: 0, goals: 0, assists: 0, matches: [] };

  return (
    <div className="min-h-screen bg-[#04060F] pt-20 pb-8 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-[#A0A0A5] hover:text-white" data-testid="pc-back-btn"><ArrowLeft size={20} /></Button>
          <div className="flex-1">
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-white uppercase tracking-wide flex items-center gap-3">
              <SoccerBall size={32} className="text-[#32D74B]" weight="fill" /> Player Career
            </h1>
            <p className="text-[#A0A0A5] mt-1">{player.name} • {team.name} • Season {careerState.season || 1}</p>
          </div>
          <Button variant="outline" onClick={() => { if (window.confirm('Reset career? This erases your player and progress.')) { onResetCareer(); navigate('/player-career/select'); } }} className="bg-transparent border-[#C8102E] text-[#C8102E]" data-testid="pc-reset-btn">
            <ArrowCounterClockwise size={14} className="mr-1" /> Reset
          </Button>
          <Button onClick={() => navigate('/save-load')} className="bg-[#1A9E5E] hover:bg-[#0F7D4B] text-white font-heading font-bold uppercase ml-2" data-testid="pc-save-btn">
            <Trophy size={14} className="mr-1" /> Save
          </Button>
          <Button
            onClick={() => {
              const choice = window.confirm('Save current career before starting a new one?\n\nOK = Save first (open Save manager)\nCancel = Continue without saving');
              if (choice) {
                navigate('/save');
              } else {
                if (window.confirm('Are you sure? The current career will be erased.')) {
                  onResetCareer();
                  navigate('/player-career/select');
                }
              }
            }}
            className="bg-[#D4A933] hover:bg-[#F0D07A] text-black font-heading font-bold uppercase ml-2"
            data-testid="pc-new-career-btn"
          >
            <Star size={14} className="mr-1" weight="fill" /> New Career
          </Button>
        </div>

        <Card className="bg-gradient-to-br from-[#121216] to-[#0A0A0C] border-[#2C2C35] p-6 mb-6" data-testid="pc-hero">
          <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-6 items-center">
            <div className="text-center">
              <div className="text-5xl mb-1">{player.flag}</div>
              <p className="font-heading font-black text-2xl text-white uppercase leading-tight">{player.name}</p>
              <p className="text-xs text-[#A0A0A5] mt-1">{player.position} • Age {player.age}</p>
              <div className="mt-3 flex justify-center gap-3">
                <div className="bg-[#0A0A0C] border border-[#FFD700]/40 rounded-sm px-3 py-1.5">
                  <p className="text-[9px] text-[#6E6E73] uppercase">OVR</p>
                  <p className="font-mono font-black text-xl text-[#FFD700] leading-none">{player.overall}</p>
                </div>
                <div className="bg-[#0A0A0C] border border-[#32D74B]/40 rounded-sm px-3 py-1.5">
                  <p className="text-[9px] text-[#6E6E73] uppercase">POT</p>
                  <p className="font-mono font-black text-xl text-[#32D74B] leading-none">{player.potential}</p>
                </div>
              </div>
            </div>
            <div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard label="Matches" value={seasonMatches.length} color="text-white" />
                <StatCard label="Goals" value={seasonGoals} color="text-[#FFD700]" />
                <StatCard label="Assists" value={seasonAssists} color="text-[#32D74B]" />
                <StatCard label="Avg Rating" value={seasonAvgRating ? seasonAvgRating.toFixed(2) : '—'} color="text-[#007AFF]" />
              </div>
              <div className="mt-4 flex flex-wrap gap-2 items-center">
                <Badge className="bg-[#C8102E] text-white">Team: {team.name}</Badge>
                <Badge className="bg-[#007AFF] text-white">League Rank: #{rank}</Badge>
                <Badge className="bg-[#FFD700] text-black">Week {careerState.currentWeek}/38</Badge>
                {intl.caps > 0 && <Badge className="bg-[#AF52DE] text-white">{player.nationality}: {intl.caps} caps</Badge>}
              </div>
            </div>
          </div>
        </Card>

        <Card className="bg-[#121216] border-[#2C2C35] p-5 mb-6" data-testid="pc-next-match">
          <h2 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
            <Calendar size={16} className="text-[#C8102E]" /> Next Match
          </h2>
          {nextMatch ? (
            <NextMatchPanel nextMatch={nextMatch} teams={careerState.teams} onPlay={startMatch} onSkip={skipMatch} playerTeamId={team.id} teamById={teamById} />
          ) : seasonComplete ? (
            <div>
              <div className="text-center mb-4">
                <Trophy size={32} className="text-[#D4A933] mx-auto mb-2" weight="fill" />
                <p className="text-white font-heading uppercase">Season Complete!</p>
                <Button onClick={advanceSeason} className="mt-3 bg-[#1A9E5E] hover:bg-[#0F7D4B] text-white font-heading font-bold uppercase" data-testid="pc-next-season-btn">
                  <FastForward size={14} className="mr-1" /> Start Next Season
                </Button>
              </div>
              {seasonAwards && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4" data-testid="pc-season-awards">
                  <AwardsList title="Top Scorers" icon="🥇" list={seasonAwards.topScorers} careerId={careerState.playerId} statKey="goals" color="text-[#D4A933]" />
                  <AwardsList title="Top Assists" icon="🎯" list={seasonAwards.topAssists} careerId={careerState.playerId} statKey="assists" color="text-[#1A9E5E]" />
                  <AwardsList title="Clean Sheets" icon="🧤" list={seasonAwards.cleanSheets} careerId={careerState.playerId} statKey="cleanSheets" color="text-[#2E5AAC]" />
                  <POTYCard poty={seasonAwards.poty} careerId={careerState.playerId} />
                </div>
              )}
            </div>
          ) : <p className="text-[#8FA2C2] text-sm">No upcoming matches.</p>}
        </Card>

        <Tabs defaultValue="season" className="w-full">
          <TabsList className="bg-[#112240] border border-[#1E3A6B] w-full grid grid-cols-8 mb-4">
            <TabsTrigger value="season" className="data-[state=active]:bg-[#D4A933] data-[state=active]:text-black font-heading uppercase text-xs" data-testid="pc-tab-season">Season</TabsTrigger>
            <TabsTrigger value="standings" className="data-[state=active]:bg-[#2E5AAC] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-standings">Table</TabsTrigger>
            <TabsTrigger value="comps" className="data-[state=active]:bg-[#1A9E5E] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-comps">Comps</TabsTrigger>
            <TabsTrigger value="teams" className="data-[state=active]:bg-[#FF6B00] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-teams">Teams</TabsTrigger>
            <TabsTrigger value="fixtures" className="data-[state=active]:bg-[#C8102E] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-fixtures">Fixtures</TabsTrigger>
            <TabsTrigger value="totw" className="data-[state=active]:bg-[#F0D07A] data-[state=active]:text-black font-heading uppercase text-xs" data-testid="pc-tab-totw">TotW</TabsTrigger>
            <TabsTrigger value="international" className="data-[state=active]:bg-[#AF52DE] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-intl">Intl</TabsTrigger>
            <TabsTrigger value="archive" className="data-[state=active]:bg-[#1A9E5E] data-[state=active]:text-white font-heading uppercase text-xs" data-testid="pc-tab-archive">Career</TabsTrigger>
          </TabsList>

          <TabsContent value="season">
            {/* Comp Progress Timeline — shows player's bracket position in each competition */}
            <Card className="bg-[#121216] border-[#2C2C35] p-4 mb-3" data-testid="pc-comp-progress">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                <Trophy size={14} className="text-[#D4A933]" weight="fill" /> Competition Progress · Season {careerState.season}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2">
                {(() => {
                  const c = careerState.competitions || {};
                  const tiles = [
                    { key: 'cup', label: 'EWFL CUP', data: c.cup, color: '#C8102E', stages: ['R1','R2','QF','SF','FINAL'], current: c.cup?.eliminated ? 'ELIM' : (c.cup?.won ? '🏆 WON' : ['R1','R2','QF','SF','FINAL'][c.cup?.currentRoundIdx ?? 0]) },
                    { key: 'ucl', label: 'CHAMPIONS LG', data: c.ucl, color: '#1A9E5E', stages: ['GROUP','R16','QF','SF','FINAL'], current: c.ucl?.qualified === false ? 'DNQ' : c.ucl?.eliminated ? 'ELIM' : c.ucl?.won ? '🏆 WON' : (c.ucl?.groupPlayed < 4 ? `GROUP ${c.ucl?.groupPlayed ?? 0}/4` : ['R16','QF','SF','FINAL'][c.ucl?.currentKoIdx ?? 0]) },
                    { key: 'uel', label: 'EUROPA LG', data: c.uel, color: '#FF6B00', stages: ['R16','QF','SF','FINAL'], current: c.uel?.qualified === false ? 'DNQ' : c.uel?.eliminated ? 'ELIM' : c.uel?.won ? '🏆 WON' : ['R16','QF','SF','FINAL'][c.uel?.currentIdx ?? 0] },
                    { key: 'uecl', label: 'CONFERENCE', data: c.uecl, color: '#32D74B', stages: ['R16','QF','SF','FINAL'], current: c.uecl?.qualified === false ? 'DNQ' : c.uecl?.eliminated ? 'ELIM' : c.uecl?.won ? '🏆 WON' : ['R16','QF','SF','FINAL'][c.uecl?.currentIdx ?? 0] },
                    { key: 'cwc', label: 'CLUB WC', data: c.cwc, color: '#2E5AAC', stages: ['GROUP','SF','FINAL'], current: c.cwc?.qualified === false ? 'DNQ' : c.cwc?.eliminated ? 'ELIM' : c.cwc?.won ? '🏆 WON' : (c.cwc?.groupPlayed < 3 ? `GROUP ${c.cwc?.groupPlayed ?? 0}/3` : ['SF','FINAL'][c.cwc?.currentKoIdx ?? 0]) },
                  ];
                  return tiles.map(t => {
                    const activeIdx = t.data?.won ? t.stages.length - 1 : t.data?.eliminated ? -1 : t.stages.findIndex(s => String(t.current).toUpperCase().startsWith(s));
                    const colored = t.data?.qualified === false ? '#3A3A40' : t.color;
                    return (
                      <div key={t.key} className="bg-[#0A0A0C] border rounded-sm overflow-hidden" style={{ borderColor: colored + '55' }} data-testid={`pc-progress-${t.key}`}>
                        <div className="px-2.5 py-1 text-[10px] font-heading font-black tracking-[0.18em]" style={{ background: colored, color: colored === '#3A3A40' ? '#A0A0A5' : '#fff' }}>
                          {t.label}
                        </div>
                        <div className="p-2">
                          <div className="flex items-center justify-between mb-1.5">
                            <span className={`font-heading font-black text-[14px] ${t.data?.won ? 'text-[#FFD700]' : t.data?.eliminated ? 'text-[#FF3B30]' : t.data?.qualified === false ? 'text-[#6E6E73]' : 'text-white'}`}>{t.current}</span>
                          </div>
                          <div className="flex items-center gap-0.5">
                            {t.stages.map((s, i) => (
                              <React.Fragment key={s}>
                                <div className={`flex-1 h-1 rounded-full ${i <= activeIdx ? '' : 'bg-[#1A1A1F]'}`} style={{ background: i <= activeIdx ? colored : undefined }} />
                              </React.Fragment>
                            ))}
                          </div>
                          <div className="flex justify-between mt-1">
                            {t.stages.map(s => (
                              <span key={s} className="text-[8px] text-[#6E6E73] font-mono">{s[0]}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </Card>

            {/* Golden Boot race tracker — top 5 scorers in EWFL league */}
            <Card className="bg-[#121216] border-[#2C2C35] p-4 mb-3" data-testid="pc-golden-boot">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                <Trophy size={14} className="text-[#FFD700]" weight="fill" /> Golden Boot Race · EWFL League
                <Badge className="bg-[#FFD700] text-black text-[9px] ml-auto">LIVE</Badge>
              </h3>
              {(() => {
                const scorers = careerState.teams.flatMap(t =>
                  (t.players || []).map(p => ({
                    id: p.id, name: p.name, position: p.position, teamName: t.name,
                    goals: p.stats?.league?.goals || 0,
                    assists: p.stats?.league?.assists || 0,
                    apps: p.stats?.league?.matchesPlayed || 0,
                    isPlayer: p.id === player.id,
                  }))
                ).filter(p => p.goals > 0 || p.isPlayer).sort((a, b) => b.goals - a.goals || b.assists - a.assists).slice(0, 8);
                const max = Math.max(1, scorers[0]?.goals || 1);
                if (!scorers.length) {
                  return <p className="text-sm text-[#6E6E73] text-center py-5 italic">The race is waiting for a first goal.</p>;
                }
                let list = scorers;
                if (!list.some(p => p.isPlayer)) {
                  const me = careerState.teams.find(t => t.id === team.id)?.players?.find(p => p.id === player.id);
                  if (me) {
                    list = list.slice(0, 7).concat([{
                      id: me.id, name: me.name, position: me.position, teamName: team.name,
                      goals: me.stats?.league?.goals || 0,
                      assists: me.stats?.league?.assists || 0,
                      apps: me.stats?.league?.matchesPlayed || 0,
                      isPlayer: true,
                    }]);
                  }
                }
                return (
                  <div className="space-y-1.5">
                    {list.map((p, i) => {
                      const pct = Math.round((p.goals / max) * 100);
                      const rankClr = i === 0 ? '#FFD700' : i === 1 ? '#C0C0C0' : i === 2 ? '#CD7F32' : '#6E6E73';
                      const medal = i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : null;
                      return (
                        <div key={p.id + i} className={`grid grid-cols-[30px_1fr_60px] gap-2 items-center ${p.isPlayer ? 'bg-[#D4A933]/10 border border-[#D4A933]/40 rounded-sm p-1 pr-2' : 'px-1'}`} data-testid={`pc-gb-row-${p.id}`}>
                          <span className="font-mono font-black text-center" style={{ color: rankClr }}>{medal || `#${i + 1}`}</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`truncate text-xs ${p.isPlayer ? 'text-[#D4A933] font-bold' : 'text-white'}`}>{p.name}</span>
                              <span className="text-[10px] text-[#6E6E73] font-mono shrink-0 truncate max-w-[120px]">{p.teamName}</span>
                              <span className="text-[9px] text-[#7A8FAB] font-mono shrink-0">{p.apps}P</span>
                              <span className="text-[9px] text-[#4DA3FF] font-mono shrink-0">{p.assists}A</span>
                            </div>
                            <div className="h-1.5 bg-[#0A0A0C] rounded-full overflow-hidden">
                              <div className="h-full rounded-full transition-all duration-500" style={{
                                width: `${Math.max(2, pct)}%`,
                                background: p.isPlayer
                                  ? 'linear-gradient(90deg, #D4A933, #FFD700)'
                                  : i === 0 ? 'linear-gradient(90deg, #C8102E, #FFD700)'
                                  : 'linear-gradient(90deg, #2C2C35, #6E6E73)'
                              }} />
                            </div>
                          </div>
                          <span className={`font-mono font-black text-right ${p.isPlayer ? 'text-[#FFD700] text-lg' : 'text-white text-sm'}`}>
                            {p.goals}<span className="text-[10px] text-[#6E6E73] ml-1">G</span>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </Card>

            <Card className="bg-[#121216] border-[#2C2C35] p-4">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3">Your Match Log — Season {careerState.season}</h3>
              <ScrollArea className="h-[420px]">
                {seasonMatches.length === 0 ? (
                  <p className="text-[#6E6E73] text-sm text-center py-10">Your match log will appear here.</p>
                ) : (
                  <div className="space-y-1.5 pr-2">
                    {[...seasonMatches].reverse().map((m, i) => {
                      const home = teamById.get(m.homeTeamId) || careerState.teams.find(t => t.id === m.homeTeamId);
                      const away = teamById.get(m.awayTeamId) || careerState.teams.find(t => t.id === m.awayTeamId);
                      const comp = m.competition || 'league';
                      const meta = buildFixtureMeta({}, comp);
                      return (
                        <div key={i} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 flex items-center gap-2 text-xs" data-testid={`pc-match-log-${i}`}>
                          <span className="font-mono text-[#6E6E73] w-10 shrink-0">W{m.week}</span>
                          <Badge className={`${meta._compBg} ${meta._compTextColor} text-[9px] font-bold px-1.5 py-0 shrink-0`} data-testid={`pc-match-log-badge-${i}`}>{meta._compLabel}</Badge>
                          <span className="flex-1 text-white truncate">{home?.name || 'Team'} <span className="font-mono text-[#FFD700]">{m.homeScore}-{m.awayScore}</span> {away?.name || 'Team'}</span>
                          <span className="text-[#FFD700] font-bold">{m.playerGoals}G</span>
                          <span className="text-[#32D74B] font-bold">{m.playerAssists}A</span>
                          <span className={`font-mono font-bold w-10 text-right ${m.playerRating >= 8 ? 'text-[#FFD700]' : m.playerRating >= 7 ? 'text-[#32D74B]' : m.playerRating >= 6 ? 'text-white' : 'text-[#C8102E]'}`}>{m.playerRating.toFixed(1)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="standings">
            <Card className="bg-[#121216] border-[#2C2C35] p-4">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3">J.League Standings</h3>
              <ScrollArea className="h-[420px] pr-2">
                <div className="space-y-1">
                  {standings.map((t, i) => (
                    <div key={t.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-sm ${t.id === team.id ? 'bg-[#32D74B]/15 border border-[#32D74B]/50' : 'bg-[#0A0A0C]'}`} data-testid={`pc-standings-${t.id}`}>
                      <span className={`font-mono font-bold w-6 text-right ${i < 4 ? 'text-[#FFD700]' : 'text-[#6E6E73]'}`}>{i + 1}</span>
                      <span className="text-sm text-white flex-1 truncate">{t.name}</span>
                      <span className="text-xs text-[#6E6E73] font-mono">{t.stats.played}</span>
                      <span className="text-xs text-[#6E6E73] font-mono">{t.stats.goalsFor}-{t.stats.goalsAgainst}</span>
                      <span className="text-sm font-mono font-bold text-white w-10 text-right">{t.stats.points}</span>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="comps">
            <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="pc-comps-tab">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-4 flex items-center gap-2">
                <Trophy size={16} className="text-[#D4A933]" weight="fill" /> Competition Tables & Player Stats
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
                {[
                  { key: 'league', label: 'LEAGUE', color: '#D4A933', text: 'text-black' },
                  { key: 'cup', label: 'ENGLISH CUP', color: '#C8102E', text: 'text-white' },
                  ...(((playedByComp.championsLeague?.length || upcomingByComp.championsLeague?.length))
                    ? [{ key: 'championsLeague', label: 'ASIAN CUP', color: '#CCFF00', text: 'text-black' }] : []),
                  ...(((playedByComp.cwc?.length || upcomingByComp.cwc?.length))
                    ? [{ key: 'cwc', label: 'CLUB WORLD CUP', color: '#2E5AAC', text: 'text-white' }] : []),
                  ...(((playedByComp.worldCup?.length || upcomingByComp.worldCup?.length))
                    ? [{ key: 'worldCup', label: 'WORLD CUP', color: '#AF52DE', text: 'text-white' }] : []),
                ].map(c => {
                  const ps = (player?.stats?.[c.key]) || { goals: 0, assists: 0, matchesPlayed: 0, yellowCards: 0, redCards: 0 };
                  return (
                    <div key={c.key} className="bg-[#0A0A0C] border rounded-sm overflow-hidden" style={{ borderColor: c.color + '55' }} data-testid={`pc-comp-card-${c.key}`}>
                      <div className="px-3 py-1.5 font-heading font-black text-[11px] tracking-[0.18em]" style={{ background: c.color, color: c.text === 'text-black' ? '#000' : '#fff' }}>
                        {c.label}
                      </div>
                      <div className="p-3 grid grid-cols-5 gap-2">
                        <SmallStat label="Apps" value={ps.matchesPlayed || 0} />
                        <SmallStat label="G" value={ps.goals || 0} />
                        <SmallStat label="A" value={ps.assists || 0} />
                        <SmallStat label="CS" value={ps.cleanSheets || 0} />
                        <SmallStat label="YC" value={ps.yellowCards || 0} />
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mb-5">
                <p className="text-[11px] text-[#A0A0A5] uppercase font-bold tracking-wider mb-2">League Standings (Top 8)</p>
                <div className="space-y-1">
                  {standings.slice(0, 8).map((t, i) => (
                    <div key={t.id} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-sm text-xs ${t.id === team.id ? 'bg-[#D4A933]/15 border border-[#D4A933]/50' : 'bg-[#0A0A0C]'}`}>
                      <span className={`font-mono font-bold w-6 text-right ${i < 4 ? 'text-[#D4A933]' : 'text-[#6E6E73]'}`}>{i + 1}</span>
                      <span className="text-white flex-1 truncate">{t.name}</span>
                      <span className="text-[#6E6E73] font-mono">{t.stats.played}</span>
                      <span className="text-[#6E6E73] font-mono">{t.stats.goalsFor}-{t.stats.goalsAgainst}</span>
                      <span className="font-mono font-bold text-white w-8 text-right">{t.stats.points}</span>
                    </div>
                  ))}
                </div>
              </div>

              {['cup', 'championsLeague', 'europaLeague', 'conferenceLeague', 'cwc'].map(comp => {
                const recent = (playedByComp[comp] || []).slice(-6).reverse();
                const upc = (upcomingByComp[comp] || []).slice(0, 4);
                if (!recent.length && !upc.length) return null;
                const meta = buildFixtureMeta({}, comp);

                // Real group rows from career state when available (now includes UEL/UECL group stages)
                const compsState = careerState.competitions || {};
                let realGroup =
                  comp === 'championsLeague'  ? (compsState.ucl?.group  || []) :
                  comp === 'europaLeague'     ? (compsState.uel?.group  || []) :
                  comp === 'conferenceLeague' ? (compsState.uecl?.group || []) :
                  comp === 'cwc'              ? (compsState.cwc?.group  || []) :
                  null;

                // Repair: if the stored group uses EWFL team IDs (legacy bug), regenerate from the international pool
                if (realGroup && realGroup.length) {
                  const intlIds = new Set(poolForComp(comp).map(t => t.id));
                  const hasIntlOpponent = realGroup.some(r => !r.isPlayer && r.teamId !== team.id && intlIds.has(r.teamId));
                  if (!hasIntlOpponent) realGroup = []; // force resynth using correct pool
                }

                // Lookup pool used to render opponent names for fixtures (uses unified teamById)
                const poolForFixture = (id) => teamById.get(id);

                // Group standings — prefer real, otherwise synthesise from played fixtures (cup/UEL/UECL one-leg)
                const compHash = (s) => { let h = 0; for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0; return h; };
                let groupRows;
                if (realGroup && realGroup.length) {
                  groupRows = [...realGroup].sort((a, b) => b.pts - a.pts || (b.gf - b.ga) - (a.gf - a.ga));
                } else {
                  // Use the right INTERNATIONAL pool for UCL/UEL/UECL/CWC so group teams are actually European
                  const otherPool = poolForComp(comp);
                  const otherTeams = (otherPool || []).filter(t => t.id !== team.id);
                  const seed = compHash(comp + team.id);
                  const groupOpps = [];
                  for (let i = 0; i < 3 && otherTeams.length > 0; i++) {
                    groupOpps.push(otherTeams[(seed + i * 7) % otherTeams.length]);
                  }
                  const playerRow = {
                    teamId: team.id,
                    name: team.name,
                    played: recent.length,
                    pts: recent.reduce((s, f) => {
                      const pHome = f.homeTeamId === team.id;
                      const ps = pHome ? f.homeScore : f.awayScore;
                      const os = pHome ? f.awayScore : f.homeScore;
                      return s + (ps > os ? 3 : ps === os ? 1 : 0);
                    }, 0),
                    gf: recent.reduce((s, f) => s + (f.homeTeamId === team.id ? f.homeScore : f.awayScore), 0),
                    ga: recent.reduce((s, f) => s + (f.homeTeamId === team.id ? f.awayScore : f.homeScore), 0),
                    isPlayer: true,
                  };
                  groupRows = [playerRow, ...groupOpps.map((t, i) => {
                    const r = (compHash(t.name + comp) >> i) & 0xff;
                    const played = playerRow.played;
                    const wins = Math.min(played, Math.max(0, Math.floor(((r % 7) + ((t.overall || 75) - 70)) / 4)));
                    const draws = Math.min(played - wins, (r >> 3) & 1);
                    const losses = Math.max(0, played - wins - draws);
                    return {
                      teamId: t.id,
                      name: t.name,
                      played,
                      pts: wins * 3 + draws,
                      gf: wins * 2 + draws + (r % 3),
                      ga: losses * 2 + ((r >> 4) % 3),
                    };
                  })].sort((a, b) => b.pts - a.pts || (b.gf - b.ga) - (a.gf - a.ga));
                }

                // Top scorers + assists + clean sheets across ALL relevant pools (EWFL + every international comp)
                const allTeamsPlusPools = [
                  ...careerState.teams,
                  ...intlPools.uclTeams,
                  ...intlPools.uelTeams,
                  ...intlPools.ueclTeams,
                  ...intlPools.cwcTeams,
                ];
                const allCompPlayers = allTeamsPlusPools.flatMap(t =>
                  (t.players || []).map(p => ({
                    id: p.id,
                    name: p.name,
                    position: p.position,
                    teamName: t.name,
                    goals: p.stats?.[comp]?.goals || 0,
                    assists: p.stats?.[comp]?.assists || 0,
                    cleanSheets: p.stats?.[comp]?.cleanSheets || 0,
                    apps: p.stats?.[comp]?.matchesPlayed || 0,
                  }))
                );
                const topScorers = [...allCompPlayers].filter(p => p.goals > 0 || p.assists > 0).sort((a, b) => b.goals - a.goals || b.assists - a.assists).slice(0, 5);
                const topAssists = [...allCompPlayers].filter(p => p.assists > 0).sort((a, b) => b.assists - a.assists || b.goals - a.goals).slice(0, 5);
                const topCleanSheets = [...allCompPlayers].filter(p => p.position === 'GK' && p.cleanSheets > 0).sort((a, b) => b.cleanSheets - a.cleanSheets || b.apps - a.apps).slice(0, 5);

                // MVP — top scorer × 3 + assists × 2 + apps × 0.2
                const mvpScored = allCompPlayers.map(p => ({ ...p, mvpScore: p.goals * 3 + p.assists * 2 + p.apps * 0.2 + p.cleanSheets * 2 })).filter(p => p.mvpScore > 0).sort((a, b) => b.mvpScore - a.mvpScore);
                const mvp = mvpScored[0];

                const isGroup = comp === 'championsLeague' || comp === 'cwc' || comp === 'europaLeague' || comp === 'conferenceLeague';
                const groupLabel = comp === 'championsLeague' ? 'GROUP → KNOCKOUTS' :
                                   comp === 'europaLeague'    ? 'GROUP → KNOCKOUTS' :
                                   comp === 'conferenceLeague'? 'GROUP → KNOCKOUTS' :
                                   comp === 'cwc'             ? 'GROUP → KNOCKOUTS' :
                                                                'CUP RUN';

                return (
                  <div key={comp} className="mb-5" data-testid={`pc-comp-${comp}`}>
                    <p className="text-[11px] uppercase font-bold tracking-wider mb-2 flex items-center gap-2" style={{ color: meta._compColor }}>
                      <Badge className={`${meta._compBg} ${meta._compTextColor} text-[9px]`}>{meta._compLabel}</Badge>
                      <span className="text-[#A0A0A5]">{groupLabel}</span>
                    </p>

                    {mvp && (
                      <div className="bg-gradient-to-r from-[#FFD700]/20 via-[#FFD700]/5 to-transparent border border-[#FFD700]/40 rounded-sm p-2.5 mb-2 flex items-center gap-3" data-testid={`pc-mvp-${comp}`}>
                        <span className="font-heading font-black text-[10px] tracking-[0.18em] text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 px-2 py-1 rounded-sm">
                          ★ MVP
                        </span>
                        <span className="text-white font-bold text-sm flex-1 truncate">{mvp.name}</span>
                        <span className="text-[#A0A0A5] text-xs truncate">{mvp.teamName}</span>
                        <span className="font-mono text-[#32D74B] font-bold text-xs">{mvp.goals}G</span>
                        <span className="font-mono text-[#4DA3FF] font-bold text-xs">{mvp.assists}A</span>
                        {mvp.cleanSheets > 0 && <span className="font-mono text-[#A0A0A5] font-bold text-xs">{mvp.cleanSheets}CS</span>}
                      </div>
                    )}

                    {isGroup && groupRows.length > 0 && (
                      <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden mb-2" data-testid={`pc-group-table-${comp}`}>
                        <div className="grid grid-cols-12 gap-1 px-2.5 py-1.5 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#7A8FAB] uppercase tracking-wider">
                          <span className="col-span-1 text-center">#</span>
                          <span className="col-span-5">Team</span>
                          <span className="col-span-1 text-center">P</span>
                          <span className="col-span-2 text-center">GF/GA</span>
                          <span className="col-span-1 text-center">GD</span>
                          <span className="col-span-2 text-center">PTS</span>
                        </div>
                        {groupRows.map((r, idx) => (
                          <div key={r.teamId} className={`grid grid-cols-12 gap-1 px-2.5 py-1.5 text-xs items-center ${r.teamId === team.id || r.isPlayer ? 'bg-[#D4A933]/15 border-l-2 border-[#D4A933]' : 'border-l-2 border-transparent'}`}>
                            <span className={`col-span-1 text-center font-mono font-bold ${idx < 2 ? 'text-[#32D74B]' : idx === 2 ? 'text-[#FFD60A]' : 'text-[#6E6E73]'}`}>{idx + 1}</span>
                            <span className="col-span-5 text-white truncate">{r.name}</span>
                            <span className="col-span-1 text-center text-[#A0A0A5] font-mono">{r.played}</span>
                            <span className="col-span-2 text-center text-[#A0A0A5] font-mono">{r.gf}-{r.ga}</span>
                            <span className="col-span-1 text-center text-[#A0A0A5] font-mono">{r.gf - r.ga >= 0 ? '+' : ''}{r.gf - r.ga}</span>
                            <span className="col-span-2 text-center text-white font-mono font-bold">{r.pts}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {topScorers.length > 0 && (
                      <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden mb-2" data-testid={`pc-top-scorers-${comp}`}>
                        <div className="px-2.5 py-1 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#FFD700] uppercase tracking-wider flex items-center gap-2">
                          <Trophy size={11} weight="fill" /> Top Scorers · All Players
                        </div>
                        {topScorers.map((p, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 px-2.5 py-1.5 text-xs items-center border-t border-[#1A1A1F]">
                            <span className="col-span-1 font-mono font-bold text-[#FFD700]">{i + 1}</span>
                            <span className="col-span-5 text-white truncate">{p.name}</span>
                            <span className="col-span-3 text-[#7A8FAB] truncate text-[11px]">{p.teamName}</span>
                            <span className="col-span-1 text-center text-[#A0A0A5] font-mono text-[10px]">{p.apps}P</span>
                            <span className="col-span-1 text-center text-[#32D74B] font-mono font-bold">{p.goals}G</span>
                            <span className="col-span-1 text-center text-[#4DA3FF] font-mono">{p.assists}A</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {topAssists.length > 0 && (
                      <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden mb-2" data-testid={`pc-top-assists-${comp}`}>
                        <div className="px-2.5 py-1 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#4DA3FF] uppercase tracking-wider flex items-center gap-2">
                          <Star size={11} weight="fill" /> Playmakers · Top Assists
                        </div>
                        {topAssists.map((p, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 px-2.5 py-1.5 text-xs items-center border-t border-[#1A1A1F]">
                            <span className="col-span-1 font-mono font-bold text-[#4DA3FF]">{i + 1}</span>
                            <span className="col-span-5 text-white truncate">{p.name}</span>
                            <span className="col-span-3 text-[#7A8FAB] truncate text-[11px]">{p.teamName}</span>
                            <span className="col-span-1 text-center text-[#A0A0A5] font-mono text-[10px]">{p.apps}P</span>
                            <span className="col-span-1 text-center text-[#4DA3FF] font-mono font-bold">{p.assists}A</span>
                            <span className="col-span-1 text-center text-[#32D74B] font-mono">{p.goals}G</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {topCleanSheets.length > 0 && (
                      <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden mb-2" data-testid={`pc-top-cleansheets-${comp}`}>
                        <div className="px-2.5 py-1 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#A0A0A5] uppercase tracking-wider flex items-center gap-2">
                          <Medal size={11} weight="fill" /> Goalkeepers · Clean Sheets
                        </div>
                        {topCleanSheets.map((p, i) => (
                          <div key={i} className="grid grid-cols-12 gap-2 px-2.5 py-1.5 text-xs items-center border-t border-[#1A1A1F]">
                            <span className="col-span-1 font-mono font-bold text-[#A0A0A5]">{i + 1}</span>
                            <span className="col-span-5 text-white truncate">{p.name}</span>
                            <span className="col-span-3 text-[#7A8FAB] truncate text-[11px]">{p.teamName}</span>
                            <span className="col-span-1 text-center text-[#A0A0A5] font-mono text-[10px]">{p.apps}P</span>
                            <span className="col-span-2 text-center text-[#32D74B] font-mono font-bold">{p.cleanSheets} CS</span>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="space-y-1">
                      {recent.map(f => {
                        const home = poolForFixture(f.homeTeamId);
                        const away = poolForFixture(f.awayTeamId);
                        return (
                          <div key={f.id} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 flex items-center gap-2 text-xs">
                            <span className="font-mono text-[#6E6E73] w-10 shrink-0">W{f.week}</span>
                            <span className="flex-1 text-white truncate">{home?.name || '?'} <span className="font-mono text-[#FFD700]">{f.homeScore ?? '-'}-{f.awayScore ?? '-'}</span> {away?.name || '?'}</span>
                            <Badge className="bg-[#1A9E5E]/30 text-[#32D74B] text-[9px]">PLAYED</Badge>
                          </div>
                        );
                      })}
                      {upc.map(f => {
                        const home = poolForFixture(f.homeTeamId);
                        const away = poolForFixture(f.awayTeamId);
                        return (
                          <div key={f.id} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 flex items-center gap-2 text-xs">
                            <span className="font-mono text-[#6E6E73] w-10 shrink-0">W{f.week}</span>
                            <span className="flex-1 text-white truncate">{home?.name || '?'} <span className="text-[#6E6E73]">vs</span> {away?.name || '?'}</span>
                            <Badge className="bg-[#FFD60A]/20 text-[#FFD60A] text-[9px]">UPCOMING</Badge>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </Card>
          </TabsContent>


          <TabsContent value="teams">
            <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="pc-teams-tab">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-4 flex items-center gap-2">
                <Trophy size={16} className="text-[#FF6B00]" weight="fill" /> All Teams In The Game · {careerState.teams.length + intlPools.uclTeams.length + intlPools.uelTeams.length + intlPools.ueclTeams.length + intlPools.cwcTeams.length}
              </h3>

              <Tabs defaultValue="ewfl" className="w-full" data-testid="pc-teams-subtabs">
                <TabsList className="bg-[#0A0A0C] border border-[#2C2C35] w-full grid grid-cols-5 mb-3">
                  <TabsTrigger value="ewfl" className="data-[state=active]:bg-[#D4A933] data-[state=active]:text-black font-heading uppercase text-[10px]">EWFL · {careerState.teams.length}</TabsTrigger>
                  <TabsTrigger value="ucl" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black font-heading uppercase text-[10px]">Asian · {intlPools.uclTeams.length}</TabsTrigger>
                  <TabsTrigger value="uel" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black font-heading uppercase text-[10px]">Asian · {intlPools.uelTeams.length}</TabsTrigger>
                  <TabsTrigger value="uecl" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black font-heading uppercase text-[10px]">Asian · {intlPools.ueclTeams.length}</TabsTrigger>
                  <TabsTrigger value="cwc" className="data-[state=active]:bg-[#2E5AAC] data-[state=active]:text-white font-heading uppercase text-[10px]">CWC · {intlPools.cwcTeams.length}</TabsTrigger>
                </TabsList>

                <TabsContent value="ewfl">
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden">
                    <div className="grid grid-cols-12 gap-1 px-2.5 py-1.5 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#7A8FAB] uppercase tracking-wider">
                      <span className="col-span-1 text-center">#</span>
                      <span className="col-span-4">Team</span>
                      <span className="col-span-1 text-center">OVR</span>
                      <span className="col-span-1 text-center">P</span>
                      <span className="col-span-1 text-center">W</span>
                      <span className="col-span-1 text-center">D</span>
                      <span className="col-span-1 text-center">L</span>
                      <span className="col-span-1 text-center">GD</span>
                      <span className="col-span-1 text-center">PTS</span>
                    </div>
                    {careerState.teams
                      .map(t => ({ ...t, gd: (t.stats?.goalsFor || 0) - (t.stats?.goalsAgainst || 0) }))
                      .sort((a, b) => (b.stats?.points || 0) - (a.stats?.points || 0) || b.gd - a.gd)
                      .map((t, i) => (
                      <div key={t.id} className={`grid grid-cols-12 gap-1 px-2.5 py-1.5 text-xs items-center border-t border-[#1A1A1F] ${t.id === team.id ? 'bg-[#D4A933]/15 border-l-2 border-[#D4A933]' : ''}`} data-testid={`pc-teams-row-${t.id}`}>
                        <span className={`col-span-1 text-center font-mono font-bold ${i < 4 ? 'text-[#32D74B]' : i < 6 ? 'text-[#FFD60A]' : i >= careerState.teams.length - 3 ? 'text-[#FF3B30]' : 'text-[#6E6E73]'}`}>{i + 1}</span>
                        <span className="col-span-4 text-white truncate flex items-center gap-2">
                          <span className="w-1 h-3 rounded-sm" style={{ background: `hsl(${(t.name.charCodeAt(0) * 23) % 360}, 70%, 55%)` }} />
                          {t.name}
                        </span>
                        <span className="col-span-1 text-center text-[#FFD700] font-mono font-bold">{t.overall || '-'}</span>
                        <span className="col-span-1 text-center text-[#A0A0A5] font-mono">{t.stats?.played || 0}</span>
                        <span className="col-span-1 text-center text-[#32D74B] font-mono">{t.stats?.won || 0}</span>
                        <span className="col-span-1 text-center text-[#FFD60A] font-mono">{t.stats?.drawn || 0}</span>
                        <span className="col-span-1 text-center text-[#FF3B30] font-mono">{t.stats?.lost || 0}</span>
                        <span className="col-span-1 text-center text-[#A0A0A5] font-mono">{t.gd >= 0 ? '+' : ''}{t.gd}</span>
                        <span className="col-span-1 text-center text-white font-mono font-bold">{t.stats?.points || 0}</span>
                      </div>
                    ))}
                  </div>
                </TabsContent>

                {[
                  { key: 'ucl', pool: intlPools.uclTeams, label: 'Asian Cup Clubs', color: '#CCFF00' },
                  { key: 'uel', pool: intlPools.uelTeams, label: 'Asian Secondary Clubs', color: '#CCFF00' },
                  { key: 'uecl', pool: intlPools.ueclTeams, label: 'Asian Tertiary Clubs', color: '#CCFF00' },
                  { key: 'cwc', pool: intlPools.cwcTeams, label: 'Club World Cup Clubs', color: '#2E5AAC' },
                ].map(({ key, pool, label, color }) => (
                  <TabsContent key={key} value={key}>
                    <p className="text-[10px] text-[#7A8FAB] uppercase tracking-widest mb-2 font-bold">{label}</p>
                    <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden">
                      <div className="grid grid-cols-12 gap-1 px-2.5 py-1.5 bg-[#1A1A1F] text-[10px] font-mono font-bold text-[#7A8FAB] uppercase tracking-wider">
                        <span className="col-span-1 text-center">#</span>
                        <span className="col-span-6">Team</span>
                        <span className="col-span-2 text-center">Country</span>
                        <span className="col-span-1 text-center">OVR</span>
                        <span className="col-span-2 text-center">Formation</span>
                      </div>
                      {[...pool].sort((a, b) => (b.overall || 0) - (a.overall || 0)).map((t, i) => (
                        <div key={t.id} className="grid grid-cols-12 gap-1 px-2.5 py-1.5 text-xs items-center border-t border-[#1A1A1F]" data-testid={`pc-teams-${key}-row-${t.id}`}>
                          <span className="col-span-1 text-center font-mono font-bold text-[#6E6E73]">{i + 1}</span>
                          <span className="col-span-6 text-white truncate flex items-center gap-2">
                            <span className="w-1 h-3 rounded-sm" style={{ background: color }} />
                            {t.name}
                          </span>
                          <span className="col-span-2 text-center text-[#A0A0A5] truncate text-[11px]">{t.flag || ''} {t.country || '—'}</span>
                          <span className="col-span-1 text-center text-[#FFD700] font-mono font-bold">{t.overall || '-'}</span>
                          <span className="col-span-2 text-center text-[#A0A0A5] font-mono text-[10px]">{t.formation || '—'}</span>
                        </div>
                      ))}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>
            </Card>
          </TabsContent>

          <TabsContent value="fixtures">
            <Card className="bg-[#121216] border-[#2C2C35] p-4">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center justify-between">
                <span>All Fixtures · League + Cup + European</span>
                <span className="text-[10px] text-[#A0A0A5] font-mono">{allUpcoming.length} pending</span>
              </h3>
              <ScrollArea className="h-[460px] pr-2">
                <div className="space-y-1.5" data-testid="pc-fixtures-list">
                  {allUpcoming.length === 0 ? (
                    <p className="text-[#6E6E73] text-sm text-center py-10">No upcoming fixtures.</p>
                  ) : allUpcoming.map(f => {
                    const home = teamById.get(f.homeTeamId);
                    const away = teamById.get(f.awayTeamId);
                    return (
                      <div key={f.id} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 flex items-center gap-3 text-xs" data-testid={`pc-fixture-${f.id}`}>
                        <span className="font-mono text-[#6E6E73] w-10 shrink-0">W{f.week}</span>
                        <Badge className={`${f._compBg} ${f._compTextColor} text-[9px] shrink-0`}>{f._compLabel}</Badge>
                        <span className="flex-1 text-white truncate">{home?.name || '?'} <span className="text-[#6E6E73]">vs</span> {away?.name || '?'}</span>
                        {f.round && <span className="text-[10px] text-[#A0A0A5] font-mono uppercase">{f.round}</span>}
                        {team.id === f.homeTeamId ? <Badge className="bg-[#C8102E] text-white text-[9px]">HOME</Badge> : team.id === f.awayTeamId ? <Badge className="bg-[#6E6E73] text-white text-[9px]">AWAY</Badge> : null}
                      </div>
                    );
                  })}
                </div>
              </ScrollArea>
            </Card>
          </TabsContent>

          <TabsContent value="totw">
            <Card className="bg-[#112240] border-[#1E3A6B] p-4" data-testid="pc-totw">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                <Star size={16} className="text-[#D4A933]" weight="fill" /> Team of the Week
              </h3>
              {!careerState.totwHistory?.length ? (
                <p className="text-[#8FA2C2] text-sm text-center py-10">Play league matches to generate the weekly Team of the Week.</p>
              ) : (
                <ScrollArea className="h-[420px] pr-2">
                  {[...careerState.totwHistory].reverse().map((totw, i) => (
                    <div key={i} className="mb-4 bg-[#060E1C] border border-[#1E3A6B] rounded-sm p-3" data-testid={`pc-totw-week-${totw.week}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-heading font-bold text-[#D4A933] text-sm uppercase">Week {totw.week}</span>
                        {totw.players.some(p => p.isCareerPlayer) && (
                          <Badge className="bg-[#D4A933] text-black text-[10px] font-bold"><Star size={10} className="mr-1" weight="fill" />You made the XI!</Badge>
                        )}
                      </div>
                      <div className="space-y-1">
                        {totw.players.map((p, pi) => (
                          <div key={pi} className={`flex items-center gap-2 px-2 py-1 rounded-sm text-xs ${p.isCareerPlayer ? 'bg-[#D4A933]/15 border border-[#D4A933]/50' : ''}`}>
                            <span className="font-mono text-[#8FA2C2] w-8 shrink-0">{p.position}</span>
                            <span className="text-base shrink-0">{p.flag}</span>
                            <span className={`flex-1 truncate ${p.isCareerPlayer ? 'text-[#D4A933] font-bold' : 'text-white'}`}>{p.playerName}</span>
                            <span className="text-[#8FA2C2] text-[10px] truncate max-w-[80px]">{p.teamName}</span>
                            {p.goals > 0 && <span className="text-[#D4A933] font-bold">{p.goals}G</span>}
                            {p.assists > 0 && <span className="text-[#1A9E5E] font-bold">{p.assists}A</span>}
                            <span className={`font-mono font-bold w-8 text-right ${p.rating >= 8.5 ? 'text-[#D4A933]' : 'text-white'}`}>{p.rating.toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </ScrollArea>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="international">
            <Card className="bg-[#121216] border-[#2C2C35] p-4" data-testid="pc-international">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                <GlobeHemisphereWest size={16} className="text-[#AF52DE]" /> International — {player.flag} {player.nationality}
              </h3>
              {!isEligibleForCallup(player) ? (
                <p className="text-[#6E6E73] text-sm text-center py-10">{player.nationality} is not eligible for international call-ups.</p>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <StatCard label="Caps" value={intl.caps} color="text-[#AF52DE]" />
                    <StatCard label="Intl Goals" value={intl.goals} color="text-[#FFD700]" />
                    <StatCard label="Intl Assists" value={intl.assists} color="text-[#32D74B]" />
                  </div>
                  <ScrollArea className="h-[320px] pr-2">
                    {!intl.matches.length ? (
                      <p className="text-[#6E6E73] text-sm text-center py-10">Keep performing to earn your first international call-up.</p>
                    ) : (
                      <div className="space-y-1.5">
                        {[...intl.matches].reverse().map((m, i) => (
                          <div key={i} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 flex items-center gap-3 text-xs">
                            <span className="font-mono text-[#6E6E73] w-10">S{m.season}W{m.week}</span>
                            <span className="flex-1 text-white">{player.nationality} vs {m.opponent}</span>
                            <span className="text-[#FFD700] font-bold">{m.goals}G</span>
                            <span className="text-[#32D74B] font-bold">{m.assists}A</span>
                            <span className={`font-mono font-bold w-10 text-right ${m.rating >= 8 ? 'text-[#FFD700]' : m.rating >= 7 ? 'text-[#32D74B]' : 'text-white'}`}>{m.rating.toFixed(1)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </>
              )}
            </Card>
          </TabsContent>

          <TabsContent value="archive">
            <Card className="bg-[#121216] border-[#2C2C35] p-4 mb-3" data-testid="pc-awards-archive">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3 flex items-center gap-2">
                <Medal size={16} className="text-[#FFD700]" weight="fill" /> Awards · MVPs & Team of the Year
              </h3>
              {(() => {
                // Compute per-competition MVPs and TOTY from current season data across all pools
                const allPools = [
                  { label: 'EWFL', teams: careerState.teams },
                  { label: 'Asian Cup', teams: intlPools.uclTeams },
                  { label: 'Asian Two', teams: intlPools.uelTeams },
                  { label: 'Asian Three', teams: intlPools.ueclTeams },
                  { label: 'CWC', teams: intlPools.cwcTeams },
                ];
                const comps = [
                  { key: 'league', label: 'EWFL LEAGUE', color: '#D4A933', text: '#000' },
                  { key: 'cup', label: 'ENGLISH CUP', color: '#C8102E', text: '#fff' },
                  { key: 'championsLeague', label: 'CHAMPIONS LG', color: '#1A9E5E', text: '#fff' },
                  { key: 'europaLeague', label: 'EUROPA LG', color: '#FF6B00', text: '#fff' },
                  { key: 'conferenceLeague', label: 'CONFERENCE', color: '#32D74B', text: '#000' },
                  { key: 'cwc', label: 'CLUB WC', color: '#2E5AAC', text: '#fff' },
                  { key: 'worldCup', label: 'WORLD CUP', color: '#AF52DE', text: '#fff' },
                ];
                const allPlayers = allPools.flatMap(({ teams }) =>
                  teams.flatMap(t => (t.players || []).map(p => ({ ...p, teamName: t.name })))
                );
                const mvpFor = (compKey) => {
                  const scored = allPlayers
                    .map(p => {
                      const s = p.stats?.[compKey] || {};
                      const apps = s.matchesPlayed || 0;
                      const g = s.goals || 0, a = s.assists || 0;
                      return { ...p, mvpScore: g * 3 + a * 2 + apps * 0.2, g, a, apps };
                    })
                    .filter(p => p.mvpScore > 0)
                    .sort((a, b) => b.mvpScore - a.mvpScore);
                  return scored[0];
                };
                const totyFor = (compKey) => {
                  // Best XI by position: 1 GK, 4 DEF, 3 MID, 3 FWD — ranked by goals+assists+apps*0.2
                  const byPos = (positions, count) => allPlayers
                    .filter(p => positions.includes(p.position))
                    .map(p => {
                      const s = p.stats?.[compKey] || {};
                      return { ...p, score: (s.goals || 0) * 3 + (s.assists || 0) * 2 + (s.matchesPlayed || 0) * 0.2 + (s.cleanSheets || 0) * 2 };
                    })
                    .filter(p => p.score > 0)
                    .sort((a, b) => b.score - a.score)
                    .slice(0, count);
                  const xi = [
                    ...byPos(['GK'], 1),
                    ...byPos(['CB','LB','RB','LWB','RWB'], 4),
                    ...byPos(['CM','CDM','CAM','LM','RM'], 3),
                    ...byPos(['ST','CF','LW','RW','LF','RF'], 3),
                  ];
                  return xi;
                };
                return (
                  <div className="space-y-3">
                    {comps.map(c => {
                      const mvp = mvpFor(c.key);
                      const toty = totyFor(c.key);
                      if (!mvp && !toty.length) return null;
                      return (
                        <div key={c.key} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm overflow-hidden" data-testid={`pc-award-${c.key}`}>
                          <div className="px-3 py-1.5 font-heading font-black text-[11px] tracking-[0.18em]" style={{ background: c.color, color: c.text }}>
                            {c.label}
                          </div>
                          <div className="p-3 space-y-2">
                            {mvp && (
                              <div className="flex items-center gap-2 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-sm px-2.5 py-1.5">
                                <span className="text-[#FFD700] font-heading font-black text-[10px] tracking-[0.2em]">★ MVP</span>
                                <span className="text-white font-bold text-sm flex-1 truncate">{mvp.name}</span>
                                <span className="text-[#A0A0A5] text-xs truncate">{mvp.teamName}</span>
                                <span className="text-[#32D74B] font-mono font-bold text-xs">{mvp.g}G</span>
                                <span className="text-[#4DA3FF] font-mono font-bold text-xs">{mvp.a}A</span>
                              </div>
                            )}
                            {toty.length >= 11 ? (
                              <div>
                                <p className="text-[10px] text-[#7A8FAB] uppercase tracking-widest font-bold mb-1">Team of the Year</p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                                  {toty.map((p, i) => (
                                    <div key={p.id + i} className={`flex items-center gap-2 px-2 py-1 rounded-sm text-xs ${p.id === player.id ? 'bg-[#D4A933]/20 border border-[#D4A933]/50' : 'bg-[#1A1A1F]'}`}>
                                      <span className="font-mono text-[9px] text-[#7A8FAB] w-6 shrink-0">{p.position}</span>
                                      <span className={`flex-1 truncate ${p.id === player.id ? 'text-[#D4A933] font-bold' : 'text-white'}`}>{p.name}</span>
                                      <span className="text-[10px] text-[#6E6E73] truncate max-w-[80px]">{p.teamName}</span>
                                      <span className="font-mono font-bold text-[#FFD700] w-7 text-right">{p.score.toFixed(0)}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : toty.length > 0 ? (
                              <p className="text-[10px] text-[#6E6E73] italic">TOTY unlocks once 11 players have appearances in this competition.</p>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </Card>

            <Card className="bg-[#121216] border-[#2C2C35] p-4">
              <h3 className="font-heading font-bold text-white uppercase text-sm mb-3">Career History</h3>
              <ScrollArea className="h-[420px] pr-2">
                {(!careerState.seasonArchive || careerState.seasonArchive.length === 0) ? (
                  <p className="text-[#6E6E73] text-sm text-center py-10">Finish a season to see your career archive.</p>
                ) : (
                  <div className="space-y-2">
                    {careerState.seasonArchive.map((s, i) => (
                      <div key={i} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="font-heading font-bold text-white text-sm">Season {s.season} • {s.year}</span>
                          <Badge className={`text-[10px] ${s.finalRank <= 2 ? 'bg-[#FFD700] text-black' : s.finalRank <= 4 ? 'bg-[#32D74B] text-black' : 'bg-[#6E6E73] text-white'}`}>#{s.finalRank}</Badge>
                        </div>
                        <p className="text-xs text-[#A0A0A5] mb-2">{s.teamName}</p>
                        <div className="grid grid-cols-4 gap-2">
                          <SmallStat label="Apps" value={s.matches} />
                          <SmallStat label="Goals" value={s.goals} />
                          <SmallStat label="Assists" value={s.assists} />
                          <SmallStat label="Rating" value={s.avgRating.toFixed(2)} />
                        </div>
                      </div>
                    ))}
                    {(careerState.transferHistory || []).length > 0 && (
                      <div className="mt-4 pt-3 border-t border-[#2C2C35]">
                        <p className="text-[10px] text-[#6E6E73] uppercase mb-2 flex items-center gap-1"><ArrowsLeftRight size={12} /> Transfers</p>
                        {careerState.transferHistory.map((t, i) => (
                          <p key={i} className="text-xs text-[#A0A0A5]">S{t.season} {t.year}: {t.fromTeam} → <span className="text-white">{t.toTeam}</span> (£{t.wage}k/wk, {t.contractYears}y)</p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </ScrollArea>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Live Match Modal with Ticker */}
      {/* Season End celebration modal */}
      <SeasonEndModal
        open={seasonEndModal}
        onClose={() => setSeasonEndModal(false)}
        onContinue={confirmAdvanceSeason}
        career={careerState}
        intlPools={intlPools}
      />

      <Dialog open={!!matchModal} onOpenChange={(o) => !o && setMatchModal(null)}>
        <DialogContent className="bg-[#121216] border-[#2C2C35] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase tracking-wide">
              {matchModal && `${matchModal.home.name} ${matchModal.matchState.homeScore} - ${matchModal.matchState.awayScore} ${matchModal.away.name}`}
            </DialogTitle>
          </DialogHeader>
          {matchModal && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_260px] gap-4">
              <div>
                <div className="flex items-center justify-between mb-2 text-xs">
                  <span className="text-[#A0A0A5]">Decision {Math.min(matchModal.idx + 1, matchModal.decisions.length)} of {matchModal.decisions.length}</span>
                  <span className="font-mono text-[#FFD700]">{matchModal.matchState.currentMinute}' / 90'</span>
                </div>
                {matchModal.idx < matchModal.decisions.length ? (
                  <>
                    <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-4 mb-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Fire size={16} className="text-[#C8102E]" weight="fill" />
                        <span className="text-xs text-[#A0A0A5] uppercase tracking-wider">{matchModal.decisions[matchModal.idx].atMinute}' Scenario</span>
                      </div>
                      <p className="text-white text-sm leading-relaxed">{matchModal.decisions[matchModal.idx].scenario}</p>
                    </div>
                    <div className="space-y-2" data-testid="pc-decision-options">
                      {matchModal.decisions[matchModal.idx].options.map(opt => (
                        <button
                          key={opt.key}
                          onClick={() => handleDecision(opt)}
                          data-testid={`pc-decision-${opt.key}`}
                          className="w-full text-left bg-[#0A0A0C] border border-[#2C2C35] hover:border-[#32D74B] rounded-sm p-3 transition-all group"
                        >
                          <div className="flex items-center justify-between">
                            <span className="text-sm text-white font-medium">{opt.label}</span>
                            <Badge className={`text-[9px] ${opt.risk === 'high' ? 'bg-[#C8102E] text-white' : opt.risk === 'med' ? 'bg-[#FFD700] text-black' : 'bg-[#32D74B] text-black'}`}>{opt.risk.toUpperCase()} RISK</Badge>
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-4 text-center">
                    <p className="text-[#A0A0A5] text-sm">Full time approaching...</p>
                  </div>
                )}
              </div>
              <div>
                <p className="text-[10px] text-[#6E6E73] uppercase mb-2 flex items-center gap-1"><Lightning size={12} className="text-[#FFD700]" weight="fill" /> Live</p>
                <div ref={tickerRef} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 h-[340px] overflow-y-auto space-y-1" data-testid="pc-match-ticker">
                  {(matchModal.matchState.tickerEvents || []).map((e, i) => (
                    <div key={i} className={`text-[11px] leading-snug flex gap-2 ${e.type === 'goal' ? 'text-[#FFD700] font-bold' : e.type === 'decision_success' ? 'text-[#32D74B]' : e.type === 'decision_fail' ? 'text-[#C8102E]' : e.type === 'assist' ? 'text-[#007AFF]' : 'text-[#A0A0A5]'}`}>
                      <span className="font-mono text-[#6E6E73] shrink-0 w-7">{e.minute}'</span>
                      <span>{e.commentary}{e.scoreAfter ? ` (${e.scoreAfter})` : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Match Result Modal */}
      <Dialog open={!!matchResultModal} onOpenChange={(o) => !o && setMatchResultModal(null)}>
        <DialogContent className="bg-[#121216] border-[#2C2C35] text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase tracking-wide">Full Time</DialogTitle>
          </DialogHeader>
          {matchResultModal && (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-xs text-[#6E6E73] uppercase tracking-widest mb-1">{matchResultModal.home.name}  vs  {matchResultModal.away.name}</p>
                <p className="font-heading font-black text-4xl text-white">
                  {matchResultModal.result.homeScore} - {matchResultModal.result.awayScore}
                </p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <SmallStat label="Your G" value={matchResultModal.result.playerGoals} />
                <SmallStat label="Your A" value={matchResultModal.result.playerAssists} />
                <SmallStat label="Rating" value={matchResultModal.result.playerRating.toFixed(1)} />
              </div>
              <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-3 max-h-40 overflow-y-auto">
                <p className="text-[10px] text-[#6E6E73] uppercase mb-1">Your Events</p>
                {matchResultModal.result.playerEvents?.length ? (
                  matchResultModal.result.playerEvents.map((e, i) => (
                    <p key={i} className="text-xs text-white py-0.5"><span className="font-mono text-[#6E6E73] mr-1">{e.minute}'</span>{e.commentary}</p>
                  ))
                ) : <p className="text-xs text-[#6E6E73]">No player events.</p>}
              </div>
              <Button onClick={() => setMatchResultModal(null)} className="w-full bg-[#32D74B] hover:bg-[#2CB53E] text-black font-heading font-bold uppercase" data-testid="pc-result-close">
                <ArrowCircleRight size={14} className="mr-1" /> Continue
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* International Call-up Modal */}
      <Dialog open={!!intlModal} onOpenChange={(o) => !o && setIntlModal(null)}>
        <DialogContent className="bg-[#121216] border-[#AF52DE]/40 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase tracking-wide flex items-center gap-2">
              <GlobeHemisphereWest size={22} className="text-[#AF52DE]" weight="fill" /> International Call-up!
            </DialogTitle>
          </DialogHeader>
          {intlModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#A0A0A5]">
                You've been called up for <span className="text-white font-bold">{player.flag} {player.nationality}</span> to face <span className="text-white font-bold">{intlModal.intlMatch.opponent}</span>!
              </p>
              <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-4 text-center">
                <p className="text-[10px] text-[#6E6E73] uppercase">Match Result (auto-sim)</p>
                <p className="font-heading font-black text-3xl text-white my-2">{intlModal.intlMatch.homeScore} - {intlModal.intlMatch.awayScore}</p>
                <p className="text-xs text-[#A0A0A5]">{player.nationality} vs {intlModal.intlMatch.opponent}</p>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <SmallStat label="Goals" value={intlModal.intlMatch.goals} />
                <SmallStat label="Assists" value={intlModal.intlMatch.assists} />
                <SmallStat label="Rating" value={intlModal.intlMatch.rating.toFixed(1)} />
              </div>
              <Button onClick={acceptIntlMatch} className="w-full bg-[#AF52DE] hover:bg-[#8E38B7] text-white font-heading font-bold uppercase" data-testid="pc-intl-accept">
                <Check size={14} className="mr-1" /> Accept Cap
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Transfer Window Modal */}
      <Dialog open={!!transferModal} onOpenChange={(o) => !o && setTransferModal(null)}>
        <DialogContent className="bg-[#121216] border-[#FFD700]/40 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading font-black uppercase tracking-wide flex items-center gap-2">
              <ArrowsLeftRight size={22} className="text-[#FFD700]" weight="fill" /> Transfer Window
            </DialogTitle>
          </DialogHeader>
          {transferModal && (
            <div className="space-y-4">
              <p className="text-sm text-[#A0A0A5]">
                {transferModal.offers?.length
                  ? `You have ${transferModal.offers.length} offer${transferModal.offers.length > 1 ? 's' : ''}. Accept one to transfer, or reject all to stay at ${team.name}.`
                  : 'No clubs are interested right now. Keep performing!'}
              </p>
              <ScrollArea className="max-h-[420px] pr-2">
                <div className="space-y-2" data-testid="pc-transfer-offers">
                  {(transferModal.offers || []).map((offer, i) => (
                    <div key={i} className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-4" data-testid={`pc-transfer-offer-${i}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <p className="font-heading font-bold text-white">{offer.teamName}</p>
                          <p className="text-[10px] text-[#6E6E73]">{offer.teamCity} • League Rank #{offer.rank} • OVR {offer.teamOvr}</p>
                        </div>
                        <Badge className={`${offer.prestige === 'Elite' ? 'bg-[#FFD700] text-black' : offer.prestige === 'Top' ? 'bg-[#32D74B] text-black' : offer.prestige === 'Mid' ? 'bg-[#007AFF] text-white' : 'bg-[#6E6E73] text-white'}`}>{offer.prestige}</Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <SmallStat label="Wage" value={`£${offer.wage}k/wk`} />
                        <SmallStat label="Bonus" value={`£${offer.signingBonus}k`} />
                        <SmallStat label="Years" value={offer.contractYears} />
                      </div>
                      <Button onClick={() => acceptTransfer(offer)} className="w-full bg-[#32D74B] hover:bg-[#2CB53E] text-black font-heading font-bold uppercase text-sm" data-testid={`pc-accept-offer-${i}`}>
                        <Check size={14} className="mr-1" /> Accept
                      </Button>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <Button onClick={rejectAllTransfers} variant="outline" className="w-full bg-transparent border-[#2C2C35] text-white hover:bg-[#1A1A1F] font-heading uppercase" data-testid="pc-reject-all-transfers">
                <X size={14} className="mr-1" /> Stay at {team.name}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-3 text-center">
    <p className="text-[10px] text-[#6E6E73] uppercase tracking-wider">{label}</p>
    <p className={`font-mono font-black text-2xl ${color}`}>{value}</p>
  </div>
);
const SmallStat = ({ label, value }) => (
  <div className="bg-[#0A0A0C] border border-[#2C2C35] rounded-sm p-2 text-center">
    <p className="text-[9px] text-[#6E6E73] uppercase">{label}</p>
    <p className="font-mono font-bold text-white text-sm">{value}</p>
  </div>
);
const NextMatchPanel = ({ nextMatch, teams, onPlay, onSkip, playerTeamId, teamById }) => {
  const home = teamById?.get(nextMatch.homeTeamId) || teams.find(t => t.id === nextMatch.homeTeamId);
  const away = teamById?.get(nextMatch.awayTeamId) || teams.find(t => t.id === nextMatch.awayTeamId);
  if (!home || !away) return null;
  const isHome = home.id === playerTeamId;
  return (
    <div>
      {nextMatch._compLabel && (
        <div className="flex justify-center mb-2">
          <Badge className="text-[10px] font-bold" style={{ backgroundColor: nextMatch._compColor, color: nextMatch._compColor === '#D4A933' || nextMatch._compColor === '#F0D07A' ? '#000' : '#fff' }}>{nextMatch._compLabel}</Badge>
        </div>
      )}
      <div className="flex items-center justify-between bg-[#060E1C] border border-[#1E3A6B] rounded-sm p-4 mb-3">
        <div className="text-center flex-1">
          <p className={`font-heading font-bold ${isHome ? 'text-[#D4A933]' : 'text-white'}`}>{home.name}</p>
          <p className="text-[10px] text-[#8FA2C2]">OVR {getTeamOverall(home)}</p>
        </div>
        <div className="mx-3 text-center">
          <p className="font-mono text-sm text-[#D4A933]">W{nextMatch.week}</p>
          <p className="text-[9px] text-[#8FA2C2] uppercase">vs</p>
        </div>
        <div className="text-center flex-1">
          <p className={`font-heading font-bold ${!isHome ? 'text-[#D4A933]' : 'text-white'}`}>{away.name}</p>
          <p className="text-[10px] text-[#8FA2C2]">OVR {getTeamOverall(away)}</p>
        </div>
      </div>
      <div className="flex gap-2">
        <Button onClick={onPlay} className="flex-1 bg-[#D4A933] hover:bg-[#F0D07A] text-black font-heading font-bold uppercase" data-testid="pc-play-match-btn">
          <Lightning size={14} className="mr-1" weight="fill" /> Play Match (Decisions)
        </Button>
        <Button onClick={onSkip} variant="outline" className="bg-transparent border-[#1E3A6B] text-white hover:bg-[#16325A]" data-testid="pc-skip-match-btn">
          <FastForward size={14} className="mr-1" /> Auto-Sim
        </Button>
      </div>
    </div>
  );
};

const AwardsList = ({ title, icon, list, careerId, statKey, color }) => (
  <div className="bg-[#060E1C] border border-[#1E3A6B] rounded-sm p-3" data-testid={`pc-award-${statKey}`}>
    <p className="font-heading font-bold text-white uppercase text-xs mb-2 flex items-center gap-2"><span className="text-base">{icon}</span> {title}</p>
    {!list?.length ? (
      <p className="text-[10px] text-[#8FA2C2] text-center py-3">No data.</p>
    ) : (
      <div className="space-y-1">
        {list.slice(0, 5).map((p, i) => {
          const isYou = p.id === careerId;
          const value = p.stats?.league?.[statKey] || 0;
          return (
            <div key={p.id} className={`flex items-center gap-2 text-xs py-0.5 ${isYou ? 'bg-[#D4A933]/15 border border-[#D4A933]/50 rounded-sm px-1' : ''}`}>
              <span className="font-mono text-[#8FA2C2] w-4">{i + 1}</span>
              <span className="text-base">{p.flag}</span>
              <span className={`flex-1 truncate ${isYou ? 'text-[#D4A933] font-bold' : 'text-white'}`}>{p.name}{isYou ? ' (YOU)' : ''}</span>
              <span className="text-[9px] text-[#8FA2C2] truncate max-w-[60px]">{p.teamName}</span>
              <span className={`font-mono font-bold w-6 text-right ${color}`}>{value}</span>
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const POTYCard = ({ poty, careerId }) => {
  const winner = poty?.[0];
  const youIndex = poty?.findIndex(p => p.id === careerId) ?? -1;
  return (
    <div className="bg-gradient-to-br from-[#D4A933]/20 to-[#060E1C] border border-[#D4A933]/50 rounded-sm p-3" data-testid="pc-award-poty">
      <p className="font-heading font-bold text-white uppercase text-xs mb-2 flex items-center gap-2"><Medal size={14} className="text-[#D4A933]" weight="fill" /> Player of the Year</p>
      {winner ? (
        <>
          <div className="text-center py-2">
            <div className="text-4xl mb-1">{winner.flag}</div>
            <p className="font-heading font-black text-lg text-[#D4A933] uppercase">{winner.name}</p>
            <p className="text-[10px] text-[#8FA2C2] uppercase tracking-wider">{winner.teamName}</p>
            <p className="text-[10px] text-[#8FA2C2] mt-1">{winner.stats?.league?.goals || 0}G • {winner.stats?.league?.assists || 0}A • OVR {winner.overall}</p>
          </div>
          {youIndex >= 0 && (
            <p className="text-[10px] text-center text-white mt-1">You finished <span className="text-[#D4A933] font-bold">#{youIndex + 1}</span> in POTY voting!</p>
          )}
        </>
      ) : <p className="text-xs text-[#8FA2C2] text-center py-4">No data yet.</p>}
    </div>
  );
};
